// packages/web-ui/src/components/inline-cards/ClarificationCard.tsx
//
// Story W2-S6 AC #4 — inline ClarificationCard.
//
// Two render modes:
//   - Pill mode    (options.length >= 2)  → intro + question + N pills + "Just do it"
//   - Free-text    (options empty)         → question + <input> + Submit
//
// Lit precedent: packages/webview-ui/src/panels/chat-panel/components/clarification-card.ts
//   - Intro text wording (line 22) — verbatim
//   - `__JUST_DO_IT__` sentinel (line 33) — verbatim
//
// SCOPE: presentational. The integration agent (W2-S6-integration) wires
// `onSubmit` to `chat.sendMessage(reply)` so the orchestrator's existing
// reply handler works unchanged.

import { useCallback, useState } from 'react';
import type { JSX, KeyboardEvent } from 'react';
import { HelpCircle } from 'lucide-react';
import { CardShell } from './CardShell.js';
import type { ClarificationEnvelopeData } from '../../state/inline-cards.js';

export interface ClarificationCardProps extends ClarificationEnvelopeData {
  onSubmit: (reply: string) => void;
}

/** Verbatim from Lit precedent line 22 — orchestrator-visible phrasing. */
export const CLARIFICATION_INTRO_TEXT =
  'I need a bit more context. What did you mean?';

/** Verbatim sentinel from Lit precedent line 33 — orchestrator reply matcher. */
export const JUST_DO_IT_SENTINEL = '__JUST_DO_IT__';

export function ClarificationCard(props: ClarificationCardProps): JSX.Element | null {
  const [submittedReply, setSubmittedReply] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  const submit = useCallback(
    (reply: string) => {
      setSubmittedReply(reply);
      props.onSubmit(reply);
    },
    [props],
  );

  const onInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') return;
      const trimmed = draft.trim();
      if (trimmed.length === 0) return;
      event.preventDefault();
      submit(trimmed);
    },
    [draft, submit],
  );

  const hasOptions = (props.options?.length ?? 0) >= 2;

  // Defensive — empty payload renders nothing + console.warn (AC #8).
  if (!hasOptions && props.question.trim().length === 0) {
    // eslint-disable-next-line no-console -- defensive guard for orchestrator bugs
    console.warn(
      'ClarificationCard: malformed payload (empty question and no options) — rendering null.',
    );
    return null;
  }

  // Submitted terminal state.
  if (submittedReply !== null) {
    return (
      <CardShell
        role="region"
        ariaLabel="Clarification needed"
        cardKind="clarification"
        testId="clarification-card-submitted"
      >
        <div className="text-sm text-taupe-700" data-testid="clarification-submitted">
          You answered: &quot;{submittedReply}&quot;
        </div>
      </CardShell>
    );
  }

  // ── Pill mode ────────────────────────────────────────────────────────
  if (hasOptions) {
    return (
      <CardShell
        role="region"
        ariaLabel="Clarification needed"
        cardKind="clarification"
        testId="clarification-card"
      >
        <div className="flex items-start gap-2 mb-2">
          <HelpCircle size={16} className="mt-0.5 text-taupe-700" aria-hidden="true" />
          <div>
            <div
              className="text-sm text-taupe-700 italic"
              data-testid="clarification-intro"
            >
              {CLARIFICATION_INTRO_TEXT}
            </div>
            <div className="text-fg" data-testid="clarification-question">
              {props.question}
            </div>
          </div>
        </div>
        <div
          role="group"
          aria-label="Clarification options"
          data-testid="clarification-pill-group"
          className="flex flex-wrap gap-2 mt-2"
        >
          {props.options?.map((option, idx) => (
            <button
              key={`${option}-${idx}`}
              type="button"
              data-testid="clarification-pill"
              data-option={option}
              aria-label={`Select: ${option}`}
              onClick={() => submit(option)}
              className="option-pill px-3 py-1 rounded-full border border-taupe-500 text-sm text-fg hover:bg-cream-100"
            >
              {option}
            </button>
          ))}
          <button
            type="button"
            data-testid="clarification-just-do-it"
            aria-label="Just do it (skip clarification)"
            onClick={() => submit(JUST_DO_IT_SENTINEL)}
            className="px-3 py-1 rounded-full border border-taupe-500 text-sm text-taupe-700 italic hover:bg-cream-100"
          >
            Just do it
          </button>
        </div>
      </CardShell>
    );
  }

  // ── Free-text mode ───────────────────────────────────────────────────
  const trimmed = draft.trim();
  return (
    <CardShell
      role="region"
      ariaLabel="Clarification needed"
      cardKind="clarification"
      testId="clarification-card"
    >
      <div className="flex items-start gap-2 mb-2">
        <HelpCircle size={16} className="mt-0.5 text-taupe-700" aria-hidden="true" />
        <div className="text-fg" data-testid="clarification-question">
          {props.question}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          aria-label="Clarification reply"
          data-testid="clarification-input"
          placeholder="Your reply…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onInputKeyDown}
          className="flex-1 px-2 py-1 rounded-none border border-taupe-500 bg-cream-50 text-fg focus:border-audi-red focus:outline-none"
        />
        <button
          type="button"
          data-testid="clarification-submit"
          aria-label="Submit clarification"
          disabled={trimmed.length === 0}
          onClick={() => submit(trimmed)}
          className="px-3 py-1.5 rounded-none bg-taupe-900 hover:bg-audi-red text-white text-xs font-semibold uppercase tracking-wide disabled:opacity-50"
        >
          Submit
        </button>
      </div>
    </CardShell>
  );
}
